import React, { useRef } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import { Close } from "@mui/icons-material";
import { useSystemTheme } from "context/SystemThemeContext";

const CustomInput = React.memo(({ 
  value, 
  onChange, 
  placeholder, 
  error, 
  helperText, 
  disabled, 
  startIcon, 
  endAdornment,
  type = "text",
  onKeyPress,
  className = "",
  fullWidth = true,
  style = {}
}) => {
  const { mode } = useSystemTheme();
  const isDarkMode = mode === "dark";
  
  const inputRef = useRef(null);

  const handleFocus = () => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  return (
    <Box 
      sx={{ 
        position: 'relative',
        width: fullWidth ? '100%' : 'auto',
        ...style
      }}
      className={className}
    >
      <Box
        onClick={handleFocus}
        sx={{
          position: 'relative',
          cursor: disabled ? 'not-allowed' : 'text',
          backgroundColor: disabled 
            ? (isDarkMode ? '#2d2d3f' : '#f5f5f5')
            : (isDarkMode ? '#060C18' : '#ffffff'),
          border: `1px solid ${error ? '#d32f2f' : (isDarkMode ? '#4a5568' : '#e0e0e0')}`,
          borderRadius: '8px',
          padding: '8px 10px',
          transition: 'all 0.2s ease',
          '&:hover': !disabled && {
            borderColor: error ? '#d32f2f' : '#219297',
            boxShadow: isDarkMode 
              ? '0 2px 8px rgba(0, 0, 0, 0.3)'
              : '0 2px 8px rgba(33, 146, 151, 0.1)',
          },
          '&:focus-within': {
            borderColor: error ? '#d32f2f' : '#219297',
            boxShadow: isDarkMode 
              ? '0 0 0 3px rgba(33, 146, 151, 0.2)'
              : '0 0 0 3px rgba(33, 146, 151, 0.1)',
            outline: 'none',
          },
        }}
      >
        {startIcon && (
          <Box 
            sx={{ 
              position: 'absolute', 
              left: '16px', 
              top: '50%', 
              transform: 'translateY(-50%)',
              color: error ? '#d32f2f' : (isDarkMode ? '#a0aec0' : '#666'),
              display: 'flex',
              alignItems: 'center',
              zIndex: 1
            }}
          >
            {startIcon}
          </Box>
        )}
        
        <input
          ref={inputRef}
          type={type}
          value={value}
          onChange={onChange}
          onKeyPress={onKeyPress}
          placeholder={placeholder}
          disabled={disabled}
          style={{
            width: '100%',
            border: 'none',
            background: 'transparent',
            fontSize: '0.95rem',
            color: isDarkMode ? '#e2e8f0' : '#333',
            outline: 'none',
            padding: startIcon ? '0 0 0 40px' : '0',
            fontFamily: 'inherit',
            lineHeight: '1.5',
            cursor: disabled ? 'not-allowed' : 'text',
            opacity: disabled ? 0.7 : 1,
          }}
          placeholderStyle={{
            color: isDarkMode ? '#a0aec0' : '#999',
          }}
        />
        
        {endAdornment && (
          <Box 
            sx={{ 
              position: 'absolute', 
              right: '8px', 
              top: '50%', 
              transform: 'translateY(-50%)',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            {endAdornment}
          </Box>
        )}
      </Box>
      
      {helperText && (
        <Typography
          sx={{
            fontSize: '0.75rem',
            color: error ? '#d32f2f' : (isDarkMode ? '#a0aec0' : '#666'),
            marginTop: '4px',
            marginLeft: '4px',
            lineHeight: 1.4,
          }}
        >
          {helperText}
        </Typography>
      )}
    </Box>
  );
});

export default CustomInput;